import { useEffect, useRef } from "react";
import {
  createChart,
  createSeriesMarkers,
  LineSeries,
  type SeriesMarker,
  type UTCTimestamp,
} from "lightweight-charts";

import type { components } from "../../api/schema";
import { formatFillAction, formatQuantity } from "../../lib/tradeFormat";
import {
  CHART_HEIGHT_PX,
  getChartColors,
  getCommonChartOptions,
} from "../charts/chartOptions";
import { useTheme } from "../theme/ThemeProvider";
import { isOpeningAction } from "../../lib/tradeAnalytics";

type FillResponse = components["schemas"]["FillResponse"];

export interface FillTimelineChartProps {
  fills: ReadonlyArray<FillResponse>;
}

interface MarkerPoint {
  time: UTCTimestamp;
  value: number;
  fill: FillResponse;
}

function toSeconds(iso: string): UTCTimestamp {
  return Math.floor(new Date(iso).getTime() / 1000) as UTCTimestamp;
}

function buildPoints(fills: ReadonlyArray<FillResponse>): MarkerPoint[] {
  const points = fills.map((fill) => ({
    time: toSeconds(fill.executed_at),
    value: Number(fill.price_amount),
    fill,
  }));
  points.sort((a, b) => a.time - b.time);
  return points;
}

// The line series requires strictly ascending, unique timestamps -- but a trade
// can have several fills in the same second (a single order split into lots),
// which would trip lightweight-charts' "data must be asc ordered by time"
// assertion. Collapse runs of equal-second points to one, keeping the last
// price in that second. Markers still render one arrow per fill, so no
// execution detail is lost. `points` must already be sorted ascending.
function toLineSeriesData(
  points: ReadonlyArray<MarkerPoint>,
): { time: UTCTimestamp; value: number }[] {
  const data: { time: UTCTimestamp; value: number }[] = [];
  for (const point of points) {
    const last = data[data.length - 1];
    if (last !== undefined && last.time === point.time) {
      last.value = point.value;
    } else {
      data.push({ time: point.time, value: point.value });
    }
  }
  return data;
}

export function FillTimelineChart({ fills }: FillTimelineChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    const colors = getChartColors();
    const chart = createChart(container, {
      ...getCommonChartOptions(),
      height: CHART_HEIGHT_PX,
      width: container.clientWidth,
    });
    const series = chart.addSeries(LineSeries, {
      color: colors.line,
      lineWidth: 2,
    });
    const points = buildPoints(fills);
    series.setData(toLineSeriesData(points));

    const markers: SeriesMarker<UTCTimestamp>[] = points.map((p) => {
      const opening = isOpeningAction(p.fill.action);
      return {
        time: p.time,
        position: opening ? "belowBar" : "aboveBar",
        color: opening ? colors.positive : colors.negative,
        shape: opening ? "arrowUp" : "arrowDown",
        text: `${formatFillAction(p.fill.action)} ${formatQuantity(p.fill.quantity)}`,
      };
    });
    createSeriesMarkers(series, markers);

    chart.timeScale().fitContent();

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.applyOptions({ width: entry.contentRect.width });
      }
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      chart.remove();
    };
  }, [fills, theme]);

  return (
    <div
      className="relative w-full"
      style={{ height: CHART_HEIGHT_PX }}
      data-testid="fill-timeline-chart"
    >
      <div ref={containerRef} className="h-full w-full" />
      {fills.length === 0 && (
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-on-surface-muted">
          No fills to display
        </p>
      )}
    </div>
  );
}
